import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, School, Stethoscope, Pill, TrendingUp } from 'lucide-react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';

const EnrollmentChart = () => {
    const [activeTab, setActiveTab] = useState('students');
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);

    const tabs = [
        { id: 'students', label: 'Current Students', icon: Users, color: '#3B82F6' },
        { id: 'schools', label: 'Current Schools', icon: School, color: '#10B981' },
        { id: 'urgentcare', label: 'Urgent Care Facilities', icon: Stethoscope, color: '#8B5CF6' },
        { id: 'pharmacies', label: 'Urgent Care Pharmacies', icon: Pill, color: '#F59E0B' }
    ];

    // Generate mock time series data (in a real app, this would come from an API)
    const generateTimeSeriesData = (type) => {
        const data = [];
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1); // 6 months ago

        for (let i = 0; i < 30; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + (i * 6)); // Every 6 days

            let baseValue = 0;
            let growthRate = 0;

            switch (type) {
                case 'students':
                    baseValue = 50;
                    growthRate = 0.15;
                    break;
                case 'schools':
                    baseValue = 10;
                    growthRate = 0.08;
                    break;
                case 'providers':
                    baseValue = 5;
                    growthRate = 0.05;
                    break;
                case 'pharmacies':
                    baseValue = 3;
                    growthRate = 0.03;
                    break;
            }

            const value = Math.floor(baseValue * Math.pow(1 + growthRate, i) + Math.random() * 10);

            data.push({
                date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                value: value,
                fullDate: date.toISOString().split('T')[0]
            });
        }

        return data;
    };

    useEffect(() => {
        setLoading(true);
        // Simulate API call delay
        setTimeout(() => {
            const data = generateTimeSeriesData(activeTab);
            setChartData(data);
            setLoading(false);
        }, 500);
    }, [activeTab]);

    const activeTabConfig = tabs.find(tab => tab.id === activeTab);

    return (
        <div className="bg-white rounded-2xl shadow-lg p-6">
            {/* Chart Header with Tabs */}
            <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Enrollment Growth Over Time</h3>

                {/* Tab Navigation */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;

                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${isActive
                                    ? 'text-white shadow-md'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                style={{
                                    backgroundColor: isActive ? tab.color : 'transparent'
                                }}
                            >
                                <Icon size={16} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Chart Container */}
            <div className="relative">
                {loading ? (
                    <div className="flex items-center justify-center h-80">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="h-80"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 20,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="date"
                                    stroke="#6b7280"
                                    fontSize={12}
                                />
                                <YAxis
                                    stroke="#6b7280"
                                    fontSize={12}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                    }}
                                    labelStyle={{ color: '#374151', fontWeight: 'bold' }}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke={activeTabConfig.color}
                                    strokeWidth={3}
                                    dot={{ fill: activeTabConfig.color, strokeWidth: 2, r: 4 }}
                                    activeDot={{ r: 6, stroke: activeTabConfig.color, strokeWidth: 2 }}
                                    name={activeTabConfig.label}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </motion.div>
                )}
            </div>

            {/* Chart Summary */}
            {!loading && chartData.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="mt-6 p-4 bg-gray-50 rounded-lg"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <TrendingUp size={20} className="text-green-600" />
                            <span className="text-sm font-medium text-gray-700">
                                Current {activeTabConfig.label}: {chartData[chartData.length - 1]?.value || 0}
                            </span>
                        </div>
                        <div className="text-sm text-gray-600">
                            Growth: +{Math.round(((chartData[chartData.length - 1]?.value - chartData[0]?.value) / chartData[0]?.value) * 100)}% over 6 months
                        </div>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default EnrollmentChart;
